import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import EventNotification from '../../../interfaces/eventNotification/EventNotification';
import { getTimeAgo } from '../../../utilities/dateUtils';
import AvatarImage from '../../ui/Avatar/AvatarImage';
import { renderNotificationMessage, getToastType } from '../../../utilities/notifications/notificationUtils';
import './ToastNotification.css'

interface ToastNotificationProps {
    notification: EventNotification;
    onClose?: () => void;
}

const ToastNotification: React.FC<ToastNotificationProps> = ({ notification, onClose }) => {
    console.log('ToastNotification component loaded for:', notification.type);
    const navigate = useNavigate();
    const [isVisible, setIsVisible] = useState(true);
    const author = notification.notificationResources?.find(r => r.resourceType === 'user');
    const toastType = getToastType(notification);

    // Preload avatar image if author exists
    React.useEffect(() => {
        if (author?.resourceId) {
            const img = new Image();
            img.src = `/api/user/avatar/${author.resourceId}`;
        }
    }, [author?.resourceId]);

    const handleClick = () => {
        const primaryResource = notification.notificationResources?.find(r => r.resourceType === 'task');
        if (primaryResource?.resourceId) {
            navigate(primaryResource.resourceId);
        }
    };

    if (!isVisible) {
        return null;
    }

    return (
        <div className={`toast-notification toast-${toastType}`} onClick={handleClick}>
            <div className="toast-left">
                <div className="toast-icon">
                    {author?.resourceId ? (
                        (() => {
                            console.log('Rendering AvatarImage for author:', author.resourceId);
                            return <AvatarImage userId={author.resourceId} size={"sm"} />;
                        })()
                    ) : (
                        (() => {
                            console.log('Rendering status icon for toastType:', toastType);
                            if (toastType === 'success') {
                                return (
                                    <span className="icon success-icon">
                                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <g clipPath="url(#clip0_488_3683)">
                                                <path d="M10.0013 18.3333C14.6037 18.3333 18.3346 14.6023 18.3346 9.99996C18.3346 5.39759 14.6037 1.66663 10.0013 1.66663C5.39893 1.66663 1.66797 5.39759 1.66797 9.99996C1.66797 14.6023 5.39893 18.3333 10.0013 18.3333Z" 
                                                      stroke="#00A63E" 
                                                      strokeWidth="1.66667" 
                                                      strokeLinecap="round" 
                                                      strokeLinejoin="round"/>
                                                <path d="M7.5 10L9.16667 11.6667L12.5 8.33334" 
                                                      stroke="#00A63E" 
                                                      strokeWidth="1.66667" 
                                                      strokeLinecap="round" 
                                                      strokeLinejoin="round"/>
                                            </g>
                                            <defs>
                                                <clipPath id="clip0_488_3683">
                                                    <rect width="20" height="20" fill="white"/>
                                                </clipPath>
                                            </defs>
                                        </svg>
                                    </span>
                                );
                            } else if (toastType === 'error') {
                                return (
                                    <span className="icon error-icon">
                                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <g clipPath="url(#clip0_488_3710)">
                                                <path d="M10.0013 18.3334C14.6037 18.3334 18.3346 14.6024 18.3346 10C18.3346 5.39765 14.6037 1.66669 10.0013 1.66669C5.39893 1.66669 1.66797 5.39765 1.66797 10C1.66797 14.6024 5.39893 18.3334 10.0013 18.3334Z" 
                                                      stroke="#E7000B" 
                                                      strokeWidth="1.66667" 
                                                      strokeLinecap="round" 
                                                      strokeLinejoin="round"/>
                                                <path d="M10 6.66666V9.99999" 
                                                      stroke="#E7000B" 
                                                      strokeWidth="1.66667" 
                                                      strokeLinecap="round" 
                                                      strokeLinejoin="round"/>
                                                <path d="M10 13.3333H10.0083" 
                                                      stroke="#E7000B" 
                                                      strokeWidth="1.66667" 
                                                      strokeLinecap="round" 
                                                      strokeLinejoin="round"/>
                                            </g>
                                            <defs>
                                                <clipPath id="clip0_488_3710">
                                                    <rect width="20" height="20" fill="white"/>
                                                </clipPath>
                                            </defs>
                                        </svg>
                                    </span>
                                );
                            } else {
                                return (
                                    <span className="icon info-icon">
                                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <g clipPath="url(#clip0_488_3696)">
                                                <path d="M10.0013 18.3333C14.6037 18.3333 18.3346 14.6023 18.3346 9.99996C18.3346 5.39759 14.6037 1.66663 10.0013 1.66663C5.39893 1.66663 1.66797 5.39759 1.66797 9.99996C1.66797 14.6023 5.39893 18.3333 10.0013 18.3333Z" 
                                                      stroke="#155DFC" 
                                                      strokeWidth="1.66667" 
                                                      strokeLinecap="round" 
                                                      strokeLinejoin="round"/>
                                                <path d="M10 13.3333V10" 
                                                      stroke="#155DFC" 
                                                      strokeWidth="1.66667" 
                                                      strokeLinecap="round" 
                                                      strokeLinejoin="round"/>
                                                <path d="M10 6.66666H10.0083" 
                                                      stroke="#155DFC" 
                                                      strokeWidth="1.66667" 
                                                      strokeLinecap="round" 
                                                      strokeLinejoin="round"/>
                                            </g>
                                            <defs>
                                                <clipPath id="clip0_488_3696">
                                                    <rect width="20" height="20" fill="white"/>
                                                </clipPath>
                                            </defs>
                                        </svg>
                                    </span>
                                );
                            }
                        })()
                    )}
                </div>
                <div>
                    <div className="toast-title">
                        {renderNotificationMessage(notification)}
                    </div>
                    <div className="toast-text">
                        {getTimeAgo(notification.createdAt)}
                    </div>
                </div>
            </div>
            <span className="close" onClick={(e) => {
                e.stopPropagation();
                setIsVisible(false);
                if (onClose) {
                    onClose();
                }
            }}>×</span>
        </div>
    );
};

export default ToastNotification;
